import { faFileInvoiceDollar } from '@fortawesome/pro-regular-svg-icons';
import { isInvoiceCreditMemo } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { CardChevron } from '../components/CardChevron';
import { EmptyState } from '../components/EmptyState';
import {
	HistoryFilters,
	historyRangeParamUpdates,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import {
	derivePaymentStatus,
	InvoiceDueChip,
	InvoicePaymentStatusPill,
} from '../components/InvoiceStatusChips';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { appScrollToTop } from '../lib/appScroll';
import { formatCurrency } from '../lib/formatCurrency';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Invoice history page with date range and payment status filters.
 * Shows invoices across all assigned clients.
 */
export function InvoicesPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const searchQuery = searchParams.get('q') ?? '';
	// Relative range key ('' = the default, last 3 months) resolved to
	// concrete ISO bounds for the API (PD-14674). 'custom' carries explicit
	// from/to bounds (PD-14899).
	const rangeParam = searchParams.get('range') ?? '';
	const fromParam = searchParams.get('from') ?? '';
	const toParam = searchParams.get('to') ?? '';
	const { dateFrom, dateTo } = resolveHistoryRange(
		rangeParam,
		fromParam,
		toParam,
	);
	const paymentStatus = searchParams.get('paymentStatus') ?? '';
	const clientIdParam = searchParams.get('clientId') ?? '';
	const typeParam = searchParams.get('type') ?? '';

	const [searchInput, setSearchInput] = useState(searchQuery);

	const { data, isLoading } = useQuery({
		queryKey: [
			'invoices',
			{
				page: currentPage,
				search: searchQuery,
				dateFrom,
				dateTo,
				paymentStatus,
				clientId: clientIdParam,
				type: typeParam,
			},
		],
		queryFn: () =>
			portalInvoiceService.getInvoices(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				search: searchQuery.length > 0 ? searchQuery : undefined,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
				paymentStatus:
					paymentStatus.length > 0
						? (paymentStatus as 'paid' | 'unpaid' | 'partial')
						: undefined,
				type:
					typeParam.length > 0
						? (typeParam as 'invoice' | 'creditMemo')
						: undefined,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const invoices = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handleSearchSubmit = useCallback(() => {
		updateParams({
			q: searchInput.trim().length > 0 ? searchInput.trim() : null,
			page: null,
		});
	}, [searchInput, updateParams]);

	// Commit the whole filter tray at once (PD-14701). The default date range
	// ('' / '3m') and empty selects clear their params for a clean URL.
	const handleApplyFilters = useCallback(
		(next: Record<string, string>) => {
			const clientId = next.clientId;
			const type = next.type;
			const status = next.paymentStatus;
			updateParams({
				...historyRangeParamUpdates(next),
				clientId: clientId.length > 0 ? clientId : null,
				type: type.length > 0 ? type : null,
				paymentStatus: status.length > 0 ? status : null,
				page: null,
			});
		},
		[updateParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			appScrollToTop('smooth');
		},
		[updateParams],
	);

	// Prompt login if not authenticated
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			{/* PD-14057 — pay several invoices at once / add money on account.
			    Rendered in the title row on BOTH breakpoints (mobile: the
			    MobilePageHeader action slot; desktop: beside the h1) instead
			    of costing its own row under the header. */}
			<MobilePageHeader
				title="Invoices"
				backPath="/account"
				action={
					<button
						type="button"
						onClick={() => {
							void navigate('/payments/make');
						}}
						className="px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
					>
						{t('Make a Payment')}
					</button>
				}
			/>
			<AccountBreadcrumb current="Invoices" />
			<div className="hidden md:flex items-center justify-between mb-4">
				<h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
					{t('Invoices')}
				</h1>
				<button
					type="button"
					onClick={() => {
						void navigate('/payments/make');
					}}
					className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
				>
					{t('Make a Payment')}
				</button>
			</div>

			{/* Search + one filter tray holding date range / location / type /
			    payment status, with Apply-Cancel (PD-14701). */}
			<div className="mb-4">
				<HistoryFilters
					searchValue={searchInput}
					onSearchChange={setSearchInput}
					onSearchSubmit={handleSearchSubmit}
					searchPlaceholder={t('Search by invoice # or item...')}
					fields={[
						{ kind: 'range', key: 'range', label: 'Date Range' },
						...(availableClients.length > 1
							? [
									{
										kind: 'location' as const,
										key: 'clientId' as const,
										label: 'Location',
										clients: availableClients,
									},
								]
							: []),
						{
							kind: 'select',
							key: 'type',
							label: 'Type',
							options: [
								{ value: '', label: 'All' },
								{ value: 'invoice', label: 'Invoices' },
								{ value: 'creditMemo', label: 'Credit Memos' },
							],
						},
						{
							kind: 'select',
							key: 'paymentStatus',
							label: 'Payment Status',
							options: [
								{ value: '', label: 'All' },
								{ value: 'paid', label: 'Paid' },
								{ value: 'unpaid', label: 'Unpaid' },
								{ value: 'partial', label: 'Partial' },
							],
						},
					]}
					values={{
						range: rangeParam,
						from: fromParam,
						to: toParam,
						clientId: clientIdParam,
						type: typeParam,
						paymentStatus,
					}}
					onApply={handleApplyFilters}
				/>
			</div>

			{/* Loading — skeleton cards shaped like the list (PD-14692) */}
			{isLoading && <HistoryListSkeleton />}

			{/* Invoice list */}
			{!isLoading && invoices.length > 0 && (
				<div className="space-y-2">
					{invoices.map((invoice) => {
						// A credit memo is `type='2'` or a negative-total row (some
						// ERP feeds emit credits as `type='1'` with a negative total).
						const isCreditMemo =
							isInvoiceCreditMemo(invoice.type) ||
							(invoice.total != null && parseFloat(invoice.total) < 0);
						// PD-14697: unpaid/partial rows carry the relative due-date
						// chip, which replaces the plain "Due:" meta text; paid rows
						// keep the plain date (urgency is moot once settled).
						const showsDueChip =
							!isCreditMemo && derivePaymentStatus(invoice) !== 'paid';
						return (
							<button
								key={invoice.id}
								type="button"
								onClick={() => {
									void navigate(`/invoices/${invoice.id}`);
								}}
								className="w-full text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:shadow-sm transition-shadow"
							>
								<div className="flex items-start justify-between gap-3">
									{/* flex-1 pins the amount + chevron to the card's right
									    edge; without it justify-between floats the amount
									    mid-card at a width that varies per row (PD-14241). */}
									<div className="flex-1 min-w-0">
										<div className="flex items-center flex-wrap gap-2 mb-1">
											<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{isCreditMemo ? t('Credit Memo') : t('Invoice')} #
												{t(invoice.externalId ?? String(invoice.id))}
											</span>
											{/* Payment status is meaningless for a credit memo —
											    the "Credit Memo" label above already identifies it. */}
											{!isCreditMemo && (
												<InvoicePaymentStatusPill invoice={invoice} />
											)}
											{showsDueChip && <InvoiceDueChip invoice={invoice} />}
										</div>
										{invoice.clientName != null && (
											<p className="text-xs text-brand-muted">
												{t(invoice.clientName)}
											</p>
										)}
										<p className="text-xs text-brand-muted mt-0.5">
											{t('Date')}: {t(invoice.date ?? '')}
											{invoice.dueDate != null && !showsDueChip && (
												<>
													{' · '}
													{t('Due')}: {t(invoice.dueDate)}
												</>
											)}
										</p>
									</div>
									<div className="text-right shrink-0">
										<div className="text-sm font-bold text-gray-900 dark:text-gray-100">
											{t(formatCurrency(invoice.total) ?? '-')}
										</div>
										{invoice.outstandingBalance != null &&
											parseFloat(invoice.outstandingBalance) > 0 && (
												<div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
													{t('Balance')}:{' '}
													{t(formatCurrency(invoice.outstandingBalance) ?? '')}
												</div>
											)}
										{invoice.numberOfLines != null && (
											<div className="text-xs text-gray-400 mt-0.5">
												{t(String(invoice.numberOfLines))} {t('items')}
											</div>
										)}
									</div>
									<CardChevron />
								</div>
							</button>
						);
					})}
				</div>
			)}

			{/* Empty state (PD-14699) — the filtered-empty variants offer the way
			    out (clear filters / widen the range) ahead of the truly-empty
			    message. The date range always narrows unless it's explicitly
			    'all', so that variant comes after the filter one. */}
			{!isLoading &&
				invoices.length === 0 &&
				(searchQuery.length > 0 ||
				paymentStatus.length > 0 ||
				typeParam.length > 0 ||
				clientIdParam.length > 0 ? (
					<EmptyState
						icon={faFileInvoiceDollar}
						title="No invoices match your filters"
						hint="Try removing a filter to see more results."
						actionLabel="Clear filters"
						onAction={() => {
							setSearchInput('');
							updateParams({
								q: null,
								paymentStatus: null,
								type: null,
								clientId: null,
								page: null,
							});
						}}
					/>
				) : rangeParam !== 'all' ? (
					<EmptyState
						icon={faFileInvoiceDollar}
						title="No invoices in this date range"
						hint="Older invoices may fall outside the selected range."
						actionLabel="View all time"
						onAction={() => {
							updateParams({ range: 'all', from: null, to: null, page: null });
						}}
					/>
				) : (
					<EmptyState
						icon={faFileInvoiceDollar}
						title="No invoices yet"
						hint="Your invoices will appear here once you have been billed."
					/>
				))}

			{/* Pagination */}
			{pagination != null && (
				<Paginator
					currentPage={currentPage}
					totalPages={pagination.totalPages}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	);
}
